import { useEffect, useRef, useState, useCallback } from 'react';
import { socket } from 'helpers/socket';
import { playFilterNotification } from '@/utils/notificationSounds';

interface NewItemsData {
  templateIds?: string[];
  template_id?: string;
  count?: number;
}

interface TemplateWithSound {
  id: string;
  notification_sound_enabled?: boolean;
}

interface UseNewItemsNotificationReturn {
  newItemsCounts: Record<string, number>;
  showRefreshButton: boolean;
  setShowRefreshButton: (show: boolean) => void;
  clearNewItemsCount: (templateId: string) => void;
}

/**
 * Hook to handle socket notifications for new items in search templates.
 * Manages new item counts for tabs and refresh button visibility.
 */
export const useNewItemsNotification = (
  authenticated: boolean,
  activeTemplateId: string | null,
  templates: TemplateWithSound[] = [],
): UseNewItemsNotificationReturn => {
  const [newItemsCounts, setNewItemsCounts] = useState<Record<string, number>>({});
  const [showRefreshButton, setShowRefreshButton] = useState<boolean>(false);
  const activeTemplateIdRef = useRef<string | null>(null);
  const templatesRef = useRef<TemplateWithSound[]>([]);

  // Keep refs in sync with state
  useEffect(() => {
    activeTemplateIdRef.current = activeTemplateId;
  }, [activeTemplateId]);

  useEffect(() => {
    templatesRef.current = templates;
  }, [templates]);

  // Clear templates ref on unmount to prevent sound playing on other pages
  useEffect(() => {
    return () => {
      templatesRef.current = [];
    };
  }, []);

  // Clear refresh button when template changes
  useEffect(() => {
    setShowRefreshButton(false);
  }, [activeTemplateId]);

  const clearNewItemsCount = useCallback((templateId: string) => {
    setNewItemsCounts((prev) => {
      const updated = { ...prev };
      delete updated[templateId];
      return updated;
    });
  }, []);

  // Listen for new items available in search templates
  useEffect(() => {
    if (!authenticated) return;

    const handleNewItemAvailable = (data: NewItemsData) => {
      const ids = data?.templateIds || (data?.template_id ? [data.template_id] : []);
      const itemCount = data?.count || 1;

      if (ids.length > 0) {
        const currentActiveId = activeTemplateIdRef.current;
        const currentTemplates = templatesRef.current;

        // Check if any of the notified templates have sound enabled (not muted)
        const hasNonMutedTemplate = ids.some((id) => {
          const template = currentTemplates.find((t) => t.id === id);
          // Only play sound for templates that exist in current page's list and are not muted
          return template && template.notification_sound_enabled !== false;
        });

        // Only play sound if at least one template is not muted
        if (hasNonMutedTemplate) {
          playFilterNotification();
        }

        if (currentActiveId && ids.includes(currentActiveId)) {
          setShowRefreshButton(true);
        }

        setNewItemsCounts((prev) => {
          const updated = { ...prev };
          ids.forEach((id) => {
            if (id !== currentActiveId) {
              updated[id] = (updated[id] || 0) + itemCount;
            }
          });
          return updated;
        });
      }
    };

    let intervalId: NodeJS.Timeout | null = null;

    const setupListener = () => {
      const socketInstance = socket.instance;
      if (socketInstance) {
        socketInstance.on('search_templates.new_item_available', handleNewItemAvailable);
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return true;
      }
      return false;
    };

    if (!setupListener()) {
      intervalId = setInterval(setupListener, 500);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      const socketInstance = socket.instance;
      if (socketInstance) {
        socketInstance.off('search_templates.new_item_available', handleNewItemAvailable);
      }
    };
  }, [authenticated]);

  return {
    newItemsCounts,
    showRefreshButton,
    setShowRefreshButton,
    clearNewItemsCount,
  };
};
